import axios from "axios";
import { API_URL } from "../config/config";
import { getHeaderOptions } from "./getHeaderOptions";
import Logger from "../utils/logger";
import logger from "../utils/logger";

const API_ENDPOINT = API_URL + "api/v1";

/**
 * 🎯 Build filter URL params for dashboard APIs
 * Ensures organization and team are mutually exclusive
 */
const buildDashboardFilterUrl = (
  organization: boolean,
  userId: string,
  selectedTeamId: string
): string => {
  // 🎯 VALIDATION: organization and team are mutually exclusive
  if (organization && selectedTeamId) {
    console.warn('⚠️ Invalid filter: organization and team cannot be used together. Using organization only.');
  }

  // Priority order:
  // 1. Organization + User (org-level user view)
  // 2. Team + User (team member view)
  // 3. Organization only (full org view)
  // 4. User only (single user view)
  // 5. Team only (team view)
  if (organization && userId) {
    return `&isOrganization=${organization}&userId=${userId}`;
  } else if (userId && selectedTeamId && !organization) {
    return `&userId=${userId}&teams=${selectedTeamId}`;
  } else if (organization) {
    return `&isOrganization=${organization}`; // No team param when org=true
  } else if (userId) {
    return `&userId=${userId}`;
  } else if (selectedTeamId) {
    return `&teams=${selectedTeamId}`;
  }
  return "";
};

export const fetchTeamList = () => {
  let obj = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem("auth_token") || "",
  };
  return axios.get(`${API_ENDPOINT}/team`, { headers: obj });
};

export const fetchOrganizationList = () => {
  let obj = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem("auth_token") || "",
  };
  return axios.get(`${API_ENDPOINT}/organization`, { headers: obj });
};

export const getUserOrganizationList = (params: object) => {
  let obj = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem("auth_token") || "",
  };
  return axios.get(`${API_ENDPOINT}/user/employee`, {
    headers: obj,
    params: params,
  });
};

export const fetchDashboardLeadCount = (
  fromDate: string,
  toDate: string,
  organization: boolean,
  userId: string,
  selectedTeamId: string,
  listIds?: string
) => {
  let obj = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem("auth_token") || "",
  };

  // 🎯 Use centralized filter builder
  let finelUrl = buildDashboardFilterUrl(organization, userId, selectedTeamId);

  // Add listIds parameter if provided
  if (listIds) {
    finelUrl += `&listIds=${listIds}`;
  }

  return axios.get(
    `${API_ENDPOINT}/dashboard/lead-count?fromDate=${fromDate}&toDate=${toDate}${finelUrl}`,
    { headers: obj }
  );
};

export const fetchDashboardAllActivityCount = (
  fromDate: string,
  toDate: string,
  organization: boolean,
  userId: string,
  selectedTeamId: string,
  listIds?: string
) => {
  let obj = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem("auth_token") || "",
  };
  let finelUrl =
    organization && userId
      ? `&isOrganization=${organization}&userId=${userId}`
      : userId && selectedTeamId
      ? `&userId=${userId}&teams=${selectedTeamId}`
      : organization
      ? `&isOrganization=${organization}`
      : userId
      ? `&userId=${userId}`
      : selectedTeamId
      ? `&teams=${selectedTeamId}`
      : "";
  
  // Add listIds parameter if provided
  if (listIds) {
    finelUrl += `&listIds=${listIds}`;
  }
  
  return axios.get(
    `${API_ENDPOINT}/dashboard/all-activities-count?fromDate=${fromDate}&toDate=${toDate}${finelUrl}`,
    { headers: obj }
  );
};

export const fetchDashboardSalesCount = (
  fromDate: string,
  toDate: string,
  organization: boolean,
  userId: string,
  selectedTeamId: string,
  listIds?: string
) => {
  let obj = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem("auth_token") || "",
  };
  let finelUrl =
    organization && userId
      ? `&isOrganization=${organization}&userId=${userId}`
      : userId && selectedTeamId
      ? `&userId=${userId}&teams=${selectedTeamId}`
      : organization
      ? `&isOrganization=${organization}`
      : userId
      ? `&userId=${userId}`
      : selectedTeamId
      ? `&teams=${selectedTeamId}`
      : "";
  
  // Add listIds parameter if provided
  if (listIds) {
    finelUrl += `&listIds=${listIds}`;
  }
  
  return axios.get(
    `${API_ENDPOINT}/dashboard/sales-value-count?fromDate=${fromDate}&toDate=${toDate}${finelUrl}`,
    { headers: obj }
  );
};

export const fetchDashboardGraphData = (
  fromDate: string,
  toDate: string,
  type: string,
  organization: boolean,
  userId: string,
  selectedTeamId: string
) => {
  let obj = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem("auth_token") || "",
  };
  let finelUrl =
    organization && userId
      ? `&isOrganization=${organization}&userId=${userId}`
      : userId && selectedTeamId
      ? `&userId=${userId}&teams=${selectedTeamId}`
      : organization
      ? `&isOrganization=${organization}`
      : userId
      ? `&userId=${userId}`
      : selectedTeamId
      ? `&teams=${selectedTeamId}`
      : "";
  return axios.get(
    `${API_URL}api/v2/dashboard/graph-data?fromDate=${fromDate}&toDate=${toDate}&dataType=${type}${finelUrl}`,
    { headers: obj }
  );
};

export const fetchDashboardLeadLableGraphData = (
  fromDate: string,
  toDate: string,
  organization: boolean,
  userId: string,
  selectedTeamId: string,
  listIds?: string
) => {
  let obj = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem("auth_token") || "",
  };
  let finelUrl =
    organization && userId
      ? `&isOrganization=${organization}&userId=${userId}`
      : userId && selectedTeamId
      ? `&userId=${userId}&teams=${selectedTeamId}`
      : organization
      ? `&isOrganization=${organization}`
      : userId
      ? `&userId=${userId}`
      : selectedTeamId
      ? `&teams=${selectedTeamId}`
      : "";
  
  // Add listIds parameter if provided
  if (listIds) {
    finelUrl += `&listIds=${listIds}`;
  }
  
  return axios.get(
    `${API_ENDPOINT}/dashboard/label-count?fromDate=${fromDate}&toDate=${toDate}${finelUrl}`,
    { headers: obj }
  );
};

export const fetchDashboardLeadSourceGraphData = (
  fromDate: string,
  toDate: string,
  organization: boolean,
  userId: string,
  selectedTeamId: string,
  listIds?: string
) => {
  let obj = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem("auth_token") || "",
  };
  let finelUrl =
    organization && userId
      ? `&isOrganization=${organization}&userId=${userId}`
      : userId && selectedTeamId
      ? `&userId=${userId}&teams=${selectedTeamId}`
      : organization
      ? `&isOrganization=${organization}`
      : userId
      ? `&userId=${userId}`
      : selectedTeamId
      ? `&teams=${selectedTeamId}`
      : "";
  
  // Add listIds parameter if provided
  if (listIds) {
    finelUrl += `&listIds=${listIds}`;
  }
  
  return axios.get(
    `${API_ENDPOINT}/dashboard/integration-count?fromDate=${fromDate}&toDate=${toDate}${finelUrl}`,
    { headers: obj }
  );
};

export const fetchDashboardFunnelChart = (
  fromDate: string,
  toDate: string,
  organization: boolean,
  userId: string,
  selectedTeamId: string,
  listIds?: string
) => {
  let obj = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem("auth_token") || "",
  };
  let finelUrl =
    organization && userId
      ? `&isOrganization=${organization}&userId=${userId}`
      : userId && selectedTeamId
      ? `&userId=${userId}&teams=${selectedTeamId}`
      : organization
      ? `&isOrganization=${organization}`
      : userId
      ? `&userId=${userId}`
      : selectedTeamId
      ? `&teams=${selectedTeamId}`
      : "";
  
  // Add listIds parameter if provided
  if (listIds) {
    finelUrl += `&listIds=${listIds}`;
  }
  
  return axios.get(
    `${API_ENDPOINT}/dashboard/status-count?fromDate=${fromDate}&toDate=${toDate}${finelUrl}`,
    { headers: obj }
  );
};

export const fetchDashboardTaskStatusChart = (
  fromDate: string,
  toDate: string,
  organization: boolean,
  userId: string,
  selectedTeamId: string
) => {
  let obj = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem("auth_token") || "",
  };
  let finelUrl =
    organization && userId
      ? `&isOrganization=${organization}&userId=${userId}`
      : userId && selectedTeamId
      ? `&userId=${userId}&teams=${selectedTeamId}`
      : organization
      ? `&isOrganization=${organization}`
      : userId
      ? `&userId=${userId}`
      : selectedTeamId
      ? `&teams=${selectedTeamId}`
      : "";
  return axios.get(
    `${API_ENDPOINT}/dashboard/all-task-count?fromDate=${fromDate}&toDate=${toDate}${finelUrl}`,
    {
      headers: obj,
    }
  );
};
export const fetchDashboardCallLeaderBoard = (
  fromDate: string,
  toDate: string,
  organization: boolean,
  userId: string,
  selectedTeamId: string
) => {
  let obj = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem("auth_token") || "",
  };
  let finelUrl =
    organization && userId
      ? `&isOrganization=${organization}&userId=${userId}`
      : userId && selectedTeamId
      ? `&userId=${userId}&teams=${selectedTeamId}`
      : organization
      ? `&isOrganization=${organization}`
      : userId
      ? `&userId=${userId}`
      : selectedTeamId
      ? `&teams=${selectedTeamId}`
      : "";
  return axios.get(
    `${API_ENDPOINT}/dashboard/call-leader-board?fromDate=${fromDate}&toDate=${toDate}${finelUrl}`,
    {
      headers: obj,
    }
  );
};
export const fetchDashboardQuotationStatusChart = (
  fromDate: string,
  toDate: string,
  organization: boolean,
  userId: string,
  selectedTeamId: string
) => {
  let obj = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem("auth_token") || "",
  };
  let finelUrl =
    organization && userId
      ? `&isOrganization=${organization}&userId=${userId}`
      : userId && selectedTeamId
      ? `&userId=${userId}&teams=${selectedTeamId}`
      : organization
      ? `&isOrganization=${organization}`
      : userId
      ? `&userId=${userId}`
      : selectedTeamId
      ? `&teams=${selectedTeamId}`
      : "";
  return axios.get(
    `${API_ENDPOINT}/dashboard/quotation-status-count?fromDate=${fromDate}&toDate=${toDate}${finelUrl}`,
    {
      headers: obj,
    }
  );
};

export const fetchDashboardMapcheckIn = (
  fromDate: string,
  toDate: string,
  organization: boolean,
  userId: string,
  selectedTeamId: string
) => {
  let obj = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem("auth_token") || "",
  };
  let finelUrl =
    organization && userId
      ? `&isOrganization=${organization}&userId=${userId}`
      : userId && selectedTeamId
      ? `&userId=${userId}&teams=${selectedTeamId}`
      : organization
      ? `&isOrganization=${organization}`
      : userId
      ? `&userId=${userId}`
      : selectedTeamId
      ? `&teams=${selectedTeamId}`
      : "";
  return axios.get(
    `${API_ENDPOINT}/dashboard/check-in?fromDate=${fromDate}&toDate=${toDate}${finelUrl}`,
    { headers: obj }
  );
};

export const fetchDashboardContentInfo = (
  fromDate: string,
  toDate: string,
  organization: boolean,
  userId: string,
  selectedTeamId: string
) => {
  let obj = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem("auth_token") || "",
  };
  let finelUrl =
    organization && userId
      ? `&isOrganization=${organization}&userId=${userId}`
      : userId && selectedTeamId
      ? `&userId=${userId}&teams=${selectedTeamId}`
      : organization
      ? `&isOrganization=${organization}`
      : userId
      ? `&userId=${userId}`
      : selectedTeamId
      ? `&teams=${selectedTeamId}`
      : "";
  return axios.get(
    `${API_ENDPOINT}/dashboard/content-count?fromDate=${fromDate}&toDate=${toDate}${finelUrl}`,
    { headers: obj }
  );
};
export const fetchSalesGoals = (
  fromDate: string,
  toDate: string,
  organization: boolean,
  userId: string,
  selectedTeamId: string
) => {
  let obj = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem("auth_token") || "",
  };
  let finelUrl =
    organization && userId
      ? `&isOrganization=${organization}&userId=${userId}`
      : userId && selectedTeamId
      ? `&userId=${userId}&teams=${selectedTeamId}`
      : organization
      ? `&isOrganization=${organization}`
      : userId
      ? `&userId=${userId}`
      : selectedTeamId
      ? `&teams=${selectedTeamId}`
      : "";
  return axios.get(
    `${API_ENDPOINT}/dashboard/get-goal-target?fromDate=${fromDate}&toDate=${toDate}${finelUrl}`,
    { headers: obj }
  );
};

export const setUserTargets = async (
  userId: string,
  salesTarget: number | null | undefined,
  callsTarget: number | null | undefined
) => {
  try {
    Logger.log("Setting targets for user:", userId);
    Logger.log("Sales target:", salesTarget);
    Logger.log("Calls target:", callsTarget);

    const headers = {
      ...getHeaderOptions,
      Authorization: localStorage.getItem("auth_token") || "",
    };
    
    // Create the payload array
    const payload = [];
    
    // Add sales target if provided
    if (salesTarget !== null && salesTarget !== undefined) {
      payload.push({
        userId,
        type: "sales_amount",
        frequency: "monthly",
        target: Number(salesTarget)
      });
    }
    
    // Add calls target if provided
    if (callsTarget !== null && callsTarget !== undefined) {
      payload.push({
        userId,
        type: "calls",
        frequency: "monthly",
        target: Number(callsTarget)
      });
    }
    
    Logger.log("Sending target payload:", payload);
    
    // Make the API call
    const response = await axios.post(
      `${API_ENDPOINT}/user/set-goal`,
      payload,
      { headers }
    );
    
    Logger.log("Target setting response:", response);
    return response;
  } catch (error) {
    Logger.log("Error setting targets:", error);
    throw error;
  }
};




export const fetchDashboardLeadAssignmentDistribution = (
  fromDate: string,
  toDate: string,
  organization: boolean,
  userId: string,
  selectedTeamId: string
) => {
  // ✅ KEEP: Auth headers setup
  let obj = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem("auth_token") || "",
  };
  
  // 🔄 TRANSFORMATION:
  const filterPrams = {
    isIgnoreList: true,
    date: {
      startedAt: fromDate,
      endedAt: toDate
    },
    ...(organization && { isOrganization: organization }),
    ...(userId && { userId }),
    ...(selectedTeamId && { teams: selectedTeamId })
  };

  logger.log('📡 Dashboard assignment API filterPrams:', filterPrams);

  // ✅ FIXED: Include headers with auth token
  return axios.post(
    `${API_ENDPOINT}/dashboard/lead-grouped-count`,
    {
      groupBy: "assignedTo",
      filterPrams: filterPrams
    },
    { headers: obj }  // ✅ CRITICAL: Auth headers!
  );
};


export const fetchDashboardUserPerformanceReport = (
  fromDate: string,
  toDate: string,
  organization: boolean,
  userId: string,
  selectedTeamId: string
) => {
  logger.log('📡 API call for user performance report:', {
    fromDate, toDate, organization, userId, selectedTeamId
  });

  const obj = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem("auth_token") || "",
  };

  // Build filter URL following same pattern as other dashboard APIs
  let filterUrl =
    organization && userId
      ? `&isOrganization=${organization}&userId=${userId}`
      : userId && selectedTeamId
      ? `&userId=${userId}&teams=${selectedTeamId}`
      : organization
      ? `&isOrganization=${organization}`
      : userId
      ? `&userId=${userId}`
      : selectedTeamId
      ? `&teams=${selectedTeamId}`
      : "";

  return axios.get(
    `${API_URL}api/v1/dashboard/user-performance-report?fromDate=${fromDate}&toDate=${toDate}${filterUrl}`,
    { headers: obj }
  );
};

// ============================================
// 🆕 NEW: Custom Field Grouping API (Using Existing Endpoint)
// ============================================

/**
 * Fetch dashboard data grouped by custom field values using existing lead-grouped-count endpoint
 * @param fromDate - Start date for filtering
 * @param toDate - End date for filtering
 * @param fieldName - Name of the custom field to group by (e.g., "country", "product", "type")
 * @param fieldType - Type of the custom field (e.g., "selection", "multi-selection")
 * @param organization - Whether to filter by organization
 * @param userId - Specific user ID to filter by
 * @param selectedTeamId - Team ID to filter by
 * @returns Promise with grouped data
 */
export const fetchDashboardCustomFieldGrouping = (
  fromDate: string,
  toDate: string,
  fieldName: string,
  fieldType: string,
  organization: boolean,
  userId: string,
  selectedTeamId: string
) => {
  logger.log('📡 Custom field grouping API call started (using lead-grouped-count):', {
    fromDate,
    toDate, 
    fieldName,
    fieldType,
    organization,
    userId,
    selectedTeamId
  });

  const obj = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem("auth_token") || "",
  };

  // Build filter parameters object following existing pattern used in fetchDashboardLeadAssignmentDistribution
  const filterParams: any = {
    isIgnoreList: true,
    date: {
      startedAt: fromDate,
      endedAt: toDate
    }
  };

  // Add user/team/organization filters
  if (organization && userId) {
    filterParams.isOrganization = organization;
    filterParams.userId = userId;
  } else if (userId && selectedTeamId) {
    filterParams.userId = userId;
    filterParams.teams = selectedTeamId;
  } else if (organization) {
    filterParams.isOrganization = organization;
  } else if (userId) {
    filterParams.userId = userId;
  } else if (selectedTeamId) {
    filterParams.teams = selectedTeamId;
  }

  logger.log('📡 API filterParams:', filterParams);
  logger.log('📡 Grouping by field type:', fieldType);
  logger.log('📡 Field name:', fieldName);

  // Use existing lead-grouped-count endpoint with groupBy parameter
  return axios.post(
    `${API_ENDPOINT}/dashboard/lead-grouped-count`,
    {
      groupBy: fieldType,  // 🎯 Field type (selection/multi-selection)
      fieldName: fieldName, // 🎯 Actual field value (product, country, etc.)
      filterPrams: filterParams
    },
    { headers: obj }
  ).then(response => {
    logger.log('✅ Custom field grouping API response:', response.data);
    return response;
  }).catch(error => {
    logger.error('❌ Custom field grouping API error:', error);
    throw error;
  });
};
